/**
 * PATH: packages/job-hub/src/jobs/qa/qaDragOrder.ts
 * FILE_ID: JOBHUB-QA-DRAG-ORDER-001
 * PURPOSE: Pure reorder maths for a QA drag. Children stay attached by parent id.
 */
export function placeBefore<T extends { id: string }>(
  rows: readonly T[],
  draggedId: string,
  beforeId: string | null,
): T[] {
  const from = rows.findIndex((row) => row.id === draggedId)
  if (from < 0 || draggedId === beforeId) return [...rows]
  const dragged = rows[from]
  const rest = rows.filter((row) => row.id !== draggedId)
  if (beforeId == null) return [...rest, dragged]
  const to = rest.findIndex((row) => row.id === beforeId)
  if (to < 0) return [...rows]
  const next = [...rest]
  next.splice(to, 0, dragged)
  return next
}

export function subtreeIds<T extends { id: string; parent_item?: string | null }>(
  rows: readonly T[],
  rootId: string,
): string[] {
  if (!rows.some((row) => row.id === rootId)) return []
  const children = new Map<string, string[]>()
  for (const row of rows) {
    const parent = row.parent_item
    if (!parent) continue
    const list = children.get(parent) ?? []
    list.push(row.id)
    children.set(parent, list)
  }
  const out: string[] = []
  const walk = (id: string) => {
    out.push(id)
    for (const child of children.get(id) ?? []) walk(child)
  }
  walk(rootId)
  return out
}

export function sameParent<T extends { id: string; parent_item?: string | null }>(
  rows: readonly T[],
  leftId: string,
  rightId: string,
): boolean {
  const left = rows.find((row) => row.id === leftId)
  const right = rows.find((row) => row.id === rightId)
  if (!left || !right) return false
  return (left.parent_item ?? null) === (right.parent_item ?? null)
}

export function floorOrder(
  floors: readonly { id: string }[],
  draggedId: string,
  beforeId: string | null,
): { id: string; sort_order: number }[] {
  if (floors.length === 0 || draggedId === floors[0].id) return []
  const pinned = floors[0]
  const rest = floors.slice(1)
  const target = beforeId === pinned.id ? null : beforeId
  const placed = placeBefore(rest, draggedId, target)
  if (placed.every((row, index) => row.id === rest[index]?.id)) return []
  return [pinned, ...placed].map((floor, sort_order) => ({ id: floor.id, sort_order }))
}
